import { useState, type ReactNode } from 'react'
import { Pressable } from './Pressable'

interface Props {
  label: ReactNode
  aside?: ReactNode
  defaultOpen?: boolean
  open?: boolean
  onOpenChange?: (next: boolean) => void
  children: ReactNode
  className?: string
}

/**
 * Locked collapse — variant C "Stagger cascade".
 * Header row (chevron + label + optional aside) + animated body.
 * Direct children get the `.dsCol__row` cascade automatically, but
 * callers can also wrap rows themselves for finer control.
 */
export const Collapse = ({
  label,
  aside,
  defaultOpen = false,
  open,
  onOpenChange,
  children,
  className,
}: Props) => {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen)
  const isOpen = open ?? uncontrolled
  const toggle = () => {
    const next = !isOpen
    if (open === undefined) setUncontrolled(next)
    onOpenChange?.(next)
  }
  return (
    <div className={`dsCol${isOpen ? ' is-open' : ''} ${className ?? ''}`.trim()}>
      <Pressable className="dsCol__head" onClick={toggle} aria-expanded={isOpen}>
        <span className="dsCol__chev" aria-hidden="true">›</span>
        <span className="dsCol__label">{label}</span>
        {aside && <span className="dsCol__aside">{aside}</span>}
      </Pressable>
      <div className="dsCol__body">
        <div className="dsCol__inner">{children}</div>
      </div>
    </div>
  )
}

export const CollapseRow = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={`dsCol__row ${className ?? ''}`.trim()}>{children}</div>
)
